import { AccessGroup, User } from '@components'

import {
  Button,
  ButtonsGroup,
  Color,
  Flex,
  Input,
  Modal,
  notify,
  Space,
  Spacing,
  Text,
  useModal,
} from '@billy/design-system'
import React, { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../enums/modalId'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useForm } from '../../../../hooks'
import { useCreateFolderModal } from '../../contexts/CreateFolderModalContext'
import { CreateFolderActionOriginType } from '../../enums/createFolderActionOriginType'
import { useCreateFolder } from './hooks/useCreateFolder'
import { CreateFolderForm, getDefaultValues, getValidationSchema } from './utils/formData'

interface CreateFolderActionModalProps {
  clientName: string
  users?: User[]
  isCpa: boolean
}

export const CreateFolderActionModal = ({ clientName, users, isCpa }: CreateFolderActionModalProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { close } = useModal(ModalId.DocumentsCreateFolderActionModal)
  const validationSchema = getValidationSchema(t)
  const defaultValues = getDefaultValues()
  const { Form, FormItem, handleSubmit, reset } = useForm({
    validationSchema,
    defaultValues,
  })
  const { parentFolderId } = useCreateFolderModal()
  const { createFolder, isLoading } = useCreateFolder({
    onSuccess: () => {
      close()
      reset()
    },
    onError: () => {
      notify({
        id: NotificationKeys.DocumentsCreateFolder,
        message: t('documents.modal.create_folder_action.error'),
        variant: 'error',
      })
    },
  })

  const handleCreateFolder = useCallback(
    ({ folderName }: CreateFolderForm) => {
      if (!organization) {
        return
      }

      createFolder({
        organizationId: organization.id,
        name: folderName,
        parentFolderId,
        origin: isCpa ? CreateFolderActionOriginType.CPA : CreateFolderActionOriginType.BO,
      })
    },
    [createFolder, organization, parentFolderId, isCpa],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleCreateFolder)()
  }, [handleSubmit, handleCreateFolder])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        submitForm()
      }
    },
    [submitForm],
  )

  const handleClose = useCallback(() => {
    close()
    reset()
  }, [close, reset])

  return (
    <Modal id={ModalId.DocumentsCreateFolderActionModal} size="s">
      <Modal.Header title={t('documents.modal.create_folder_action.text.title')} />
      <Modal.Body>
        <Text variant="bodyLG">
          <Trans
            i18nKey="documents.modal.create_folder_action.text.body"
            values={{ clientName }}
            components={{ strong: <strong /> }}
          />
        </Text>
        <Space size="s16" />
        {users && users.length > 0 && (
          <Flex alignItems="center" gap={Spacing.S4}>
            <AccessGroup users={users} />
            <Text variant="bodyMD" color={Color.Grey500}>
              {t('documents.modal.create_folder_action.text.subtext', { count: users.length })}
            </Text>
          </Flex>
        )}
        <Space size="s12" />
        <Form>
          <FormItem
            name="folderName"
            render={({ field }) => (
              <Input
                {...field}
                placeholder={t('documents.modal.create_folder_action.form.input.placeholder.folder_name')}
                onKeyDown={handleKeyDown}
              />
            )}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button onClick={handleClose} variant="tonal">
            {t('documents.modal.create_folder_action.button.cancel')}
          </Button>
          <Button onClick={submitForm} disabled={isLoading}>
            {t('documents.modal.create_folder_action.button.create')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
